// Which notices have a "Run all" chain in flight, at module scope so it survives the
// reader being unmounted.
//
// Every other piece of run state already lives outside the components — the per-phase
// stores are module-scoped for exactly this reason — but the fact that a CHAIN is running
// was useState inside NoticeViewer. Switch to the Parts DB tab and back and the reader
// remounts with runningAll false, so `incomingPhases` is null and every phase the chain
// has not reached yet drops its "coming" hatching and draws an empty bar. The run itself
// carries on, which is what made it confusing: the phases going were right and the phases
// waiting looked idle.
//
// Deliberately NOT persisted to storage. A chain lives in the tab that is driving it: this
// answers "is a run happening right now", and a page that was closed mid-run is not
// running one however recently it was. The suspended-run case is already answered properly
// by resumePoint(), which reads what the phases actually produced.

type Chain = {
  // The last phase number this run will reach. What PhaseNav hatches up to.
  through: number;
  // Stopped short and waiting: paused by hand, or held on a question only the user can
  // settle. Kept here because a resumed reader has to come back to the same distinction —
  // a held run is not on its way, and hatching it as "coming" would be a lie.
  paused: boolean;
};

const live = new Map<string, Chain>();

export function startChainRun(docId: string, through: number) {
  if (docId) live.set(docId, { through, paused: false });
}

export function setChainPaused(docId: string, paused: boolean) {
  const chain = docId && live.get(docId);
  if (chain) live.set(docId, { ...chain, paused });
}

export function endChainRun(docId: string) {
  live.delete(docId);
}

export const chainRun = (docId: string): Chain | null =>
  (docId && live.get(docId)) || null;
